<script>
   /*********************************************************************/
   /* Contributions modal                                               */
   /* --------------------                                              */
   /* plot variables contributions to q/h distances for selected object */
   /*********************************************************************/

   import { untrack } from 'svelte';
   import { Multilines, Rectangles, Points, Lines, TextLabels } from 'svelte-plots-basic/2d';
   import { Index, Matrix, Vector, vector, tcrossprod } from 'mdatools/arrays';
   import { range } from 'mdatools/stat';

   import { Container, Select } from 'svelte-controls-basic';

   import Modal from './Modal.svelte';
   import Axes from '../plots/Axes.svelte';
   import { escapeText } from '../utils.js';


   let {
      model,
      res,
      selected,
      ncomp,
      closeModal,
      ind = null,
      model_getter,
      res_getter,
      residuals
   } = $props();

   // type of the contributions to show
   let type = $state('residual');

   // index and x-coordinate of the selected variable
   let sel = $state(-1);

   const resIDs = $derived(res ? Object.keys(res) : null);

   // svelte-ignore state_referenced_locally
   let resID = $state(resIDs ? resIDs[0] : null);

   // mouse handle for selection of variables
   function mouseHandler(e) {

      // refuse a lone rect: the axes box and the highlight each sit alone in their group
      if (e.target.tagName !== 'rect' || e.target.parentNode.children.length < 2) {
         sel = -1;
         return;
      }

      const id = Array.from(e.target.parentNode.children).indexOf(e.target);
      if (id < 0) {
         sel = -1;
         return;
      }

      // if index is the same as the highlighted variable, do nothing
      if (sel === id) return;
      sel = id;
   }


   // set x-axis values and limits
   const xc = $derived.by(() => {
      if (!model) return null;
      const nresp = !model.nresp ? 0 : model.nresp;
      const values = vector(model.varvalues.filter((v, i) => i >= nresp));
      const n = values.length;                                      // number of variables
      const varrev = values.v[1] < values.v[0];                     // true if x-axis is reversed
      const dil = (values.v[1] - values.v[0]) / 2;                  // half distance between two first x-variables
      const dir = (values.v[n - 1] - values.v[n - 2]) / 2;          // half distance between two last x-variables
      const rng =  [values.v[0], values.v[n - 1]];
      const lim = [rng[0] - dil, rng[1] + dir];
      const width = Math.abs(rng[1] - rng[0]) / n;
      return {values, lim, width, varrev, hwidth: width * 0.5, nresp}
   });


   // compute coordinates of rectangles for selection if necessary
   const left = $derived(xc ? (
         xc.varrev ?
         xc.values.apply(v => v + xc.hwidth) :
         xc.values.apply(v => v - xc.hwidth)
      ) : null);


   // get main outcomes
   const resobj = $derived(resID ? res[resID] : null);

   // E is not stored: 'residuals' computes it for the result shown. it is kept with the resID, the
   // result and the model it came from, and an answer for an older run is dropped. 'last' is the
   // last E computed, drawn whole while the next is computed, so a switch keeps the plot mounted
   let cache = $state.raw(null);
   let last = $state.raw(null);
   let run = 0;

   $effect(() => {
      const id = resID, r = resobj, m = model, n = ++run;
      cache = null;
      if (r && m) {
         const set = (x) => {
            if (n !== run) return;
            cache = {resID: id, resobj: r, model: m, ...x};
            last = x.E ? cache : null;
         };
         // the text is drawn first, as the computation blocks the page
         requestAnimationFrame(() => setTimeout(() => {
            if (n !== run) return;
            Promise.resolve()
               .then(() => untrack(() => residuals(id)))
               .then((E) => new Matrix(E, r.nrows, r.npred).t())
               .then((E) => set({E}), (e) => { console.error(e); set({error: true}); });
         }));
      }
      return () => { run++; };
   });

   const current = $derived(cache && cache.resID === resID && cache.resobj === resobj &&
      cache.model === model ? cache : null);
   const E = $derived(current && current.E ? current.E : null);
   const failed = $derived(current ? current.error === true : false);

   // what the plot draws: the current E, or while it is computed the last one with its own result
   const shown = $derived(E ? current : resobj && last && last.model === model ? last : null);
   const shownRes = $derived(shown ? shown.resobj : null);

   // 'ind' selects rows of the current result, so the snapshot keeps the one drawn with it
   $effect(() => { if (E) current.ind = ind; });

   const T = $derived(shownRes ? new Matrix(res_getter(shownRes, "T"), shownRes.nrows, shownRes.ncomp) : null);
   const P = $derived(model ? new Matrix(model_getter(model, "P"), model.npred ? model.npred : model.ncols , model.ncomp) : null);

   // column indices for explained and residual parts
   const ind1 = $derived(ncomp ? Index.seq(1, ncomp) : null);
   const ind2 = $derived(model && ncomp < model.ncomp ? Index.seq(ncomp + 1, model.ncomp) : null);

   // define row indices
   const rows = $derived(shown ? (shown === current ? ind : shown.ind) : null);
   const indRows = $derived(shownRes ? (rows ? rows : Index.seq(1, shownRes.nrows)) : null);

   // subset T and P for explained and residual parts
   const T1 = $derived(T && ind1 ? T.subset(null, ind1) : null);
   const P1 = $derived(T && ind1 ? P.subset(null, ind1) : null);
   const T2 = $derived(T && ind2 ? T.subset(null, ind2) : null);
   const P2 = $derived(T && ind2 ? P.subset(null, ind2) : null);

   // compute explained and residual parts of data and the full data
   const Ht = $derived(T1 && P1 ? tcrossprod(P1, T1) : null);
   const Qt = $derived(shown ? (T2 && P2 ? tcrossprod(P2, T2).add(shown.E) : shown.E) : null);
   const Xt = $derived(Ht && Qt ? Ht.add(Qt) : null);

   // selected object
   const row = $derived(shownRes && selected >= 0 && selected < shownRes.nrows ? selected + 1 : 0);
   let h = $derived(Ht && row ? Ht.getcolumn(row) : null);
   let q = $derived(Qt && row ? Qt.getcolumn(row) : null);
   let xp = $derived(Xt && row ? Xt.getcolumn(row) : null);

   let yc = $derived.by(() => {
      if (!h || !q || !xp) return null;
      const vc =  type === 'explained' ?  {all: Ht, sel: h, label: 'Explained part, TP^T'} :
         (type === 'residual' ? {all: Qt, sel: q, label: 'Residuals, E'} :
         {all: Xt, sel: xp, label: 'Preprocessed data, X'});

      if (!vc.all) return null;
      const rng = range(vc.all.v);
      const height = rng[1] - rng[0];
      const lim = [rng[0] - height/50, rng[1] + height/12];
      const top = Vector.fill(rng[1], vc.sel.length);
      vc['lim'] = lim;
      vc['top'] = top;
      vc['height'] = height;
      return vc;
   });

   // y-coordinate of selected point and label
   const hx = $derived(xc && sel >= 0 ? xc.values.v[sel] : null);
   const hy = $derived(yc && sel >= 0 ? yc.sel.v[sel] : null);
   // 'varlabels' starts with the responses that 'xc' skips
   const hl = $derived(xc && sel >= 0 ? model.varlabels[sel + xc.nresp] : null);

   // set plot title
   const title = $derived(row ? `Object ${shownRes.objlabels[selected].replace("_", "-")}` : null);
   const xLabel = $derived(model ? model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : '') : null);
</script>

{#if xc}
<Modal {closeModal}>
   <div class="wrapper">
      {#if yc}
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <div class="plot-container" role="tooltip" onclick={mouseHandler}>
         <Axes {title} yLabel={yc.label} {xLabel} limY={yc ? yc.lim : [0, 1]} limX={xc.lim} fileName='plot-contributions' >

         <!-- all outcomes -->
         {#if indRows}
         <Multilines xValues={xc.values} yValues={yc.all.subset(null, indRows)} lineColor='#e0e0e0'/>
         {:else}
         <Multilines xValues={xc.values} yValues={yc.all} lineColor='#e0e0e0'/>
         {/if}

         <!-- outcomes for the selected objects -->
         <Lines xValues={xc.values} yValues={yc.sel} lineColor="crimson"  />

         <!-- rectangles to make selection possible -->
         <Rectangles {left} top={yc.top} height={yc.height} width={xc.width} faceColor='transparent' borderColor='transparent'/>

         {#if sel >= 0}
            <!-- highlighted variable -->
            <TextLabels xValues={[hx]} yValues={[yc.top.v[0]]} labels={[escapeText(hl)]} pos={3}
               textSize={0.85}/>
            <Points xValues={[hx]} yValues={[hy]} faceColor='crimson' lineColor='crimson'/>
            <Rectangles left={[left.v[sel]]} top={[yc.top.v[sel]]} height={yc.height} width={xc.width} faceColor='#ff000015'/>
         {/if}
      </Axes>
      </div>

      <div class="msg-select">
         {E ? 'click inside the plot to see a variable label' : 'Computing residuals...'}
      </div>
      {:else if resobj && !E}
      <div class="msg-state">
         {failed ? 'Residuals could not be computed.' : 'Computing residuals...'}
      </div>
      {/if}
      <div class="plot-controls">
         <div>
         <Container label='Show'>
            <Select id='type' bind:value={type} options={['full', 'explained', 'residual']} />
         </Container>
         </div>
         {#if resIDs.length > 1}
         <div>
         <Container label='For'>
            <Select id='resID' bind:value={resID} options={resIDs} />
         </Container>
         </div>
         {/if}
      </div>

   </div>
</Modal>
{/if}

<style>
   .wrapper {
      width: 95vw;
      height: 90vh;
      display: grid;
      grid-template-areas:
         "plot"
         "message"
         "controls";
      grid-template-rows: 1fr 15px min-content;
   }

   .plot-container {
      grid-area: plot;
   }

   .plot-controls {
      grid-area: controls;
      display: flex;
      width: 100%;
      justify-content: center;
      border-radius: 0.5rem;
   }

   .plot-controls > :global(.control-element) {
      background: transparent;
   }

   .msg-state {
      grid-area: plot;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #888;
   }

   .msg-select {
      grid-area: message;
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>
